import React, { useEffect, useState, useCallback } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import {
    getAuthConfig, updateAuthConfig,
    getActiveSessions, getSessionHistory, revokeSession
} from '../../services/AuthConfigService';
import { getEmptyMessage, getErrorMessage, getSuccessMessage } from '../../utils/MessageUtils';
import Pagination from '../../components/Pagination/Pagination';
import { confirmDialog } from '../../utils/ConfirmDialog';

const COMPLEXITY_LABELS = { none: 'Nenhuma', medium: 'Média', strong: 'Forte' };
const COMPLEXITY_HINTS = {
    none: 'Qualquer senha é aceita.',
    medium: 'Mínimo 8 caracteres, 1 maiúscula e 1 número.',
    strong: 'Mínimo 12 caracteres, maiúsculas, minúsculas, número e símbolo.'
};

function formatDuration(seconds) {
    if (!seconds) return '';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h && m) return `${h}h ${m}min`;
    if (h) return `${h}h`;
    return `${m}min`;
}

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR');
}

function sessionEndReason(session) {
    if (session.revokedAt) return `Revogada por ${session.revokedBy || 'admin'}`;
    if (session.logoutAt) return 'Logout';
    return '—';
}

export default function Authentication() {
    const [config, setConfig] = useState({ sessionDuration: 1800, passwordExpireDays: '', passwordMinComplexity: 'none' });
    const [sessions, setSessions] = useState([]);
    const [history, setHistory] = useState([]);
    const [historyCount, setHistoryCount] = useState(0);
    const [historyPage, setHistoryPage] = useState(1);
    const historyPageSize = 20;
    const [notification, setNotification] = useState(getEmptyMessage());

    useEffect(() => {
        getAuthConfig()
            .then(cfg => setConfig({
                sessionDuration: cfg.sessionDuration,
                passwordExpireDays: cfg.passwordExpireDays ?? '',
                passwordMinComplexity: cfg.passwordMinComplexity
            }))
            .catch(() => setNotification(getErrorMessage('Erro ao carregar configurações de autenticação.')));
    }, []);

    const loadSessions = useCallback(() => {
        getActiveSessions()
            .then(setSessions)
            .catch(() => setNotification(getErrorMessage('Erro ao carregar sessões ativas.')));
    }, []);

    useEffect(loadSessions, [loadSessions]);

    useEffect(() => {
        getSessionHistory(historyPage, historyPageSize)
            .then(result => { setHistory(result.rows); setHistoryCount(result.count); })
            .catch(() => setNotification(getErrorMessage('Erro ao carregar histórico de sessões.')));
    }, [historyPage]);

    function onConfigChange(event) {
        setConfig(prev => ({ ...prev, [event.target.id]: event.target.value }));
    }

    function onSaveConfig() {
        const payload = {
            sessionDuration: parseInt(config.sessionDuration),
            passwordExpireDays: config.passwordExpireDays === '' ? null : parseInt(config.passwordExpireDays),
            passwordMinComplexity: config.passwordMinComplexity
        };
        updateAuthConfig(payload)
            .then(() => setNotification(getSuccessMessage('Configurações salvas com sucesso!')))
            .catch(() => setNotification(getErrorMessage('Erro ao salvar configurações.')));
    }

    async function onRevokeClick(id) {
        const ok = await confirmDialog({
            title: 'Derrubar sessão',
            message: 'Derrubar esta sessão? O usuário será desconectado na próxima requisição.',
            confirmLabel: 'Derrubar sessão',
            confirmVariant: 'danger'
        });
        if (!ok) return;
        revokeSession(id)
            .then(() => {
                setNotification(getSuccessMessage('Sessão encerrada.'));
                loadSessions();
            })
            .catch(() => setNotification(getErrorMessage('Erro ao revogar sessão.')));
    }

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h1 className="h4">Autenticação</h1>
                    </div>
                </div>

                <div className="row">
                    <div className="col-12">
                        <ul className="nav nav-tabs" id="authTabs" role="tablist">
                            <li className="nav-item" role="presentation">
                                <button className="nav-link active" data-bs-toggle="tab" data-bs-target="#tabConfig" type="button" role="tab">
                                    Configurações
                                </button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button className="nav-link" data-bs-toggle="tab" data-bs-target="#tabSessions" type="button" role="tab"
                                    onClick={loadSessions}>
                                    Sessões Ativas
                                    {sessions.length > 0 && <span className="badge bg-primary ms-2">{sessions.length}</span>}
                                </button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button className="nav-link" data-bs-toggle="tab" data-bs-target="#tabHistory" type="button" role="tab">
                                    Histórico
                                </button>
                            </li>
                        </ul>

                        <div className="tab-content px-3 mb-3" id="authTabContent">

                            {/* ── ABA CONFIGURAÇÕES ── */}
                            <div className="tab-pane fade show active pt-3" id="tabConfig" role="tabpanel">
                                <div className="card card-body border-0 shadow mb-4">
                                    <div className="row mb-3">
                                        <div className="col-md-4">
                                            <label htmlFor="sessionDuration" className="form-label">
                                                Duração da sessão (segundos)
                                            </label>
                                            <input
                                                className="form-control"
                                                id="sessionDuration"
                                                type="number"
                                                min="60"
                                                max="604800"
                                                value={config.sessionDuration}
                                                onChange={onConfigChange}
                                            />
                                            <small className="text-muted">
                                                Atual: {formatDuration(parseInt(config.sessionDuration))} — só afeta novos logins
                                            </small>
                                        </div>
                                        <div className="col-md-4">
                                            <label htmlFor="passwordExpireDays" className="form-label">
                                                Expiração de senha (dias)
                                            </label>
                                            <input
                                                className="form-control"
                                                id="passwordExpireDays"
                                                type="number"
                                                min="1"
                                                placeholder="Sem expiração"
                                                value={config.passwordExpireDays}
                                                onChange={onConfigChange}
                                            />
                                            <small className="text-muted">
                                                Deixe em branco para não expirar
                                            </small>
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Complexidade mínima de senha</label>
                                            {['none', 'medium', 'strong'].map(level => (
                                                <div className="form-check" key={level}>
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="passwordMinComplexity"
                                                        id={`complexity-${level}`}
                                                        value={level}
                                                        checked={config.passwordMinComplexity === level}
                                                        onChange={() => setConfig(prev => ({ ...prev, passwordMinComplexity: level }))}
                                                    />
                                                    <label className="form-check-label" htmlFor={`complexity-${level}`}>
                                                        <strong>{COMPLEXITY_LABELS[level]}</strong>
                                                        <span className="text-muted ms-2 small">{COMPLEXITY_HINTS[level]}</span>
                                                    </label>
                                                </div>
                                            ))}
                                            <small className="text-muted">Aplica-se apenas quando o usuário altera a própria senha.</small>
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-sm-3">
                                            <button className="btn btn-gray-800 mt-2 animate-up-2" type="button" onClick={onSaveConfig}>
                                                <svg className="icon icon-xs me-2" fill="currentColor" viewBox="0 0 20 20"><path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" /></svg>
                                                Salvar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ── ABA SESSÕES ATIVAS ── */}
                            <div className="tab-pane fade pt-3" id="tabSessions" role="tabpanel">
                                <div className="card card-body border-0 shadow mb-4 table-responsive">
                                    {sessions.length === 0
                                        ? <p className="text-muted mb-0">Nenhuma sessão ativa no momento.</p>
                                        : (
                                            <table className="table table-hover">
                                                <thead>
                                                    <tr>
                                                        <th>Usuário</th>
                                                        <th>Perfil</th>
                                                        <th>Dispositivo</th>
                                                        <th>IP</th>
                                                        <th>Início</th>
                                                        <th>Última atividade</th>
                                                        <th>Ação</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sessions.map(s => (
                                                        <tr key={s.id}>
                                                            <td>{s.email}</td>
                                                            <td>
                                                                <span className={`badge bg-${s.profile === 'ADMIN' ? 'danger' : 'secondary'}`}>
                                                                    {s.profile}
                                                                </span>
                                                            </td>
                                                            <td>{s.device || '—'}</td>
                                                            <td>{s.ip || '—'}</td>
                                                            <td>{formatDate(s.loginAt)}</td>
                                                            <td>{formatDate(s.lastActivityAt)}</td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-danger"
                                                                    onClick={() => onRevokeClick(s.id)}
                                                                    title="Derrubar sessão"
                                                                >
                                                                    Derrubar
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )
                                    }
                                </div>
                            </div>

                            {/* ── ABA HISTÓRICO ── */}
                            <div className="tab-pane fade pt-3" id="tabHistory" role="tabpanel">
                                <div className="card card-body border-0 shadow mb-4 table-responsive">
                                    {history.length === 0
                                        ? <p className="text-muted mb-0">Nenhuma sessão registrada ainda.</p>
                                        : (
                                            <React.Fragment>
                                                <table className="table table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>Usuário</th>
                                                            <th>Perfil</th>
                                                            <th>Dispositivo</th>
                                                            <th>IP</th>
                                                            <th>Início</th>
                                                            <th>Fim</th>
                                                            <th>Encerramento</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {history.map(s => (
                                                            <tr key={s.id}>
                                                                <td>{s.email}</td>
                                                                <td>
                                                                    <span className={`badge bg-${s.profile === 'ADMIN' ? 'danger' : 'secondary'}`}>
                                                                        {s.profile}
                                                                    </span>
                                                                </td>
                                                                <td>{s.device || '—'}</td>
                                                                <td>{s.ip || '—'}</td>
                                                                <td>{formatDate(s.loginAt)}</td>
                                                                <td>{formatDate(s.logoutAt || s.revokedAt)}</td>
                                                                <td>{sessionEndReason(s)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                                <Pagination count={historyCount} size={historyPageSize} />
                                            </React.Fragment>
                                        )
                                    }
                                </div>
                            </div>

                        </div>
                    </div>
                </div>

                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    );
}
